import React, { useEffect, useRef, useState } from 'react'
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from '@material-ui/core'
import { makeStyles, fade } from '@material-ui/core/styles'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import AddIcon from '@material-ui/icons/Add'
import SearchIcon from '@material-ui/icons/Search'
import ApartmentIcon from '@material-ui/icons/Apartment'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import CloseIcon from '@material-ui/icons/Close'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import GlobalModal from '../Global/GlobalModal'
import Button from '../Button/Button'
import TextField from '../TextField/TextField'
import CMSText, { prismicText } from '../Global/CMSText'
import {
  FgasCapacityCertificate,
  getFgasCapacityCertificates,
  searchFgasCapacityCertificates,
  createFgasCapacityCertificate,
} from '../../redux/services/fgas'
import {
  isFgasEndDateExpiredOrToday,
  isFgasStartStrictlyBeforeEnd,
  isValidFgasSiret,
  isValidFgasFrenchPostalCode,
} from '../../utils/fgasCertificateValidation'
import { PrismicBasketData } from '../../interfaces/common/prismicBasket'
import clsx from 'clsx'

type ModalView = 'list' | 'create' | 'confirm'

type FormState = Omit<FgasCapacityCertificate, 'id' | 'supportingDocumentUrl'>

const EMPTY_FORM: FormState = {
  certificateNumber: '',
  validityStartDate: '',
  validityEndDate: '',
  holderCompanyName: '',
  holderSiret: '',
  holderAddress: {
    address: '',
    zipcode: '',
    city: '',
  },
}

const CERTS_INITIAL_COUNT = 3

const useStyles = makeStyles((theme) => {
  return {
    /** Surcharge GlobalModal : pas de bandeau gris, pas de ligne, croix plate, moins d’air titre → contenu */
    '@global': {
      '.fgas-certificate-modal .MuiPaper-root': {
        borderRadius: `${theme.spacing(1)}px !important`,
      },
      '.fgas-certificate-modal__head': {
        background: 'none !important',
        backgroundColor: 'transparent !important',
        borderBottom: 'none !important',
        paddingTop: `${theme.spacing(3)}px !important`,
        paddingBottom: theme.spacing(1),
      },
      '.fgas-certificate-modal__close': {
        background: 'none !important',
        backgroundColor: 'transparent !important',
        color: `${theme.palette.text.primary} !important`,
        borderRadius: 0,
        width: theme.spacing(3),
        height: theme.spacing(3),
        padding: theme.spacing(0.25),
        '&:hover, &:focus': {
          background: `${fade(theme.palette.common.black, 0.06)} !important`,
          backgroundColor: 'transparent !important',
        },
      },
      '.fgas-certificate-modal__content': {
        paddingTop: theme.spacing(1),
      },
    },
    description: {
      marginTop: 0,
      marginBottom: theme.spacing(3),
    },
    // ── Vue 1 : liste ──
    sectionTitle: {
      fontWeight: 600,
      marginBottom: theme.spacing(2),
    },
    certCard: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.5, 2),
      marginBottom: theme.spacing(1.5),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      cursor: 'pointer',
      transition: 'border-color 0.15s',
      '&:hover': {
        borderColor: theme.palette.secondary.dark,
      },
    },
    certCardSelected: {
      borderColor: theme.palette.secondary.dark,
    },
    certIcon: {
      color: theme.palette.secondary.dark,
      flexShrink: 0,
      marginTop: 2,
    },
    certName: {
      fontWeight: 600,
      marginBottom: theme.spacing(0.25),
    },
    certMeta: {
      color: theme.palette.text.secondary,
    },
    showMoreBtn: {
      paddingLeft: 0,
      color: theme.palette.primary.main,
      marginBottom: theme.spacing(2),
    },
    separator: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(2),
      margin: theme.spacing(2.5, 0),
      color: theme.palette.text.secondary,
    },
    separatorLine: {
      flex: 1,
    },
    addNewBtn: {
      width: '100%',
      justifyContent: 'center',
      padding: theme.spacing(1.5, 2),
    },
    cancelRow: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: theme.spacing(1.5),
    },
    // ── Vue 2 : formulaire ──
    backLink: {
      paddingLeft: 0,
      alignSelf: 'flex-start',
      width: 'auto',
      justifyContent: 'flex-start',
      textAlign: 'left',
    },
    searchWrapper: {
      position: 'relative',
    },
    searchDropdown: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      zIndex: 1400,
      maxHeight: 200,
      overflowY: 'auto',
    },
    /** Même rendu que `components/TextField/TextField` (classe `label`) */
    inputLabel: {
      display: 'block',
      cursor: 'pointer',
      textAlign: 'inherit',
      fontSize: 11,
      fontFamily: theme.typography.fontFamily,
      color: theme.typography.body1.color,
      paddingLeft: theme.spacing(1.25),
      marginBottom: theme.spacing(0.5),
    },
    /** Même hauteur visuelle que les inputs TextField (pas de minHeight ni padding vertical en plus) */
    certSearchRow: {
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      padding: theme.spacing(0, 1, 0, 1.25),
      backgroundColor: theme.extraColors.greyLight,
      borderRadius: theme.spacing(0.75),
      border: `1px solid ${theme.extraColors.greyLight}`,
    },
    certSearchIcon: {
      display: 'flex',
      alignItems: 'center',
      flexShrink: 0,
      color: theme.palette.text.secondary,
    },
    certSearchFieldInner: {
      flex: 1,
      minWidth: 0,
      marginBottom: '0 !important',
      '& > label': {
        display: 'none',
      },
      '& .inputContainer': {
        backgroundColor: 'transparent',
      },
      '& .input': {
        border: 'none',
        backgroundColor: 'transparent',
      },
    },
    /** Même padding / rayon que `.input` dans `components/TextField/TextField` */
    fileUploadArea: {
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing(1, 1.25),
      borderRadius: theme.spacing(0.75),
      cursor: 'pointer',
      border: `1px dashed ${theme.palette.divider}`,
      transition: 'border-color 0.15s',
      '&:hover': {
        borderColor: theme.palette.primary.main,
      },
    },
    fileUploadAreaDisabled: {
      cursor: 'not-allowed',
      opacity: 0.55,
      pointerEvents: 'none',
      '&:hover': {
        borderColor: theme.palette.divider,
      },
    },
    fileUploadInner: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing(1.25),
      minWidth: 0,
      maxWidth: '100%',
      flex: '0 1 auto',
    },
    fileUploadText: {
      flex: 1,
      minWidth: 0,
      fontSize: 14,
      lineHeight: 1.25,
    },
    fileUploadClear: {
      flexShrink: 0,
      padding: theme.spacing(0.5),
      marginRight: theme.spacing(-0.5),
    },
    validateBtn: {
      width: '100%',
    },
    loadingBtnLabel: {
      visibility: 'hidden',
    },
    // ── Vue 3 : confirmation ──
    confirmCard: {
      padding: theme.spacing(2),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
    },
    confirmCompanyName: {
      fontWeight: 600,
      marginBottom: theme.spacing(0.5),
    },
    checkboxContainer: {
      border: `1px solid ${theme.palette.primary.light}`,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(1.5),
      backgroundColor: fade(theme.palette.primary.main, 0.04),
    },
    checkboxLabel: {
      margin: 0,
      alignItems: 'flex-start',
      '& .MuiCheckbox-root': {
        paddingTop: 2,
      },
    },
    confirmActions: {
      display: 'flex',
      gap: theme.spacing(1.5),
    },
    confirmBackBtn: {
      flex: 1,
    },
    confirmBtn: {
      flex: 2,
    },
    error: {
      color: theme.palette.error.main,
    },
    /** Espacement vertical homogène entre les blocs (formulaire création + confirmation) */
    formVerticalStack: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      '& $description': {
        marginBottom: 0,
      },
      '& $backLink': {
        marginBottom: 0,
      },
      '& $error': {
        marginTop: 0,
        marginBottom: 0,
      },
    },
    loadingCenter: {
      display: 'flex',
      justifyContent: 'center',
      padding: theme.spacing(4, 0),
    },
  }
})

type Props = {
  open: boolean
  onClose: () => void
  onConfirm: (certificate: FgasCapacityCertificate) => Promise<void>
  basketData?: PrismicBasketData
  userEmail?: string
}

const FgasCertificateModal: React.FC<Props> = ({
  open,
  onClose,
  onConfirm,
  basketData,
  userEmail,
}) => {
  const classes = useStyles()
  const isGuest = !userEmail

  const [view, setView] = useState<ModalView>('list')

  // Vue 1
  const [certificates, setCertificates] = useState<FgasCapacityCertificate[]>(
    []
  )
  const [loadingCerts, setLoadingCerts] = useState(false)
  const [showAll, setShowAll] = useState(false)

  // Attestation retenue pour la vue confirmation (liste existante, autocomplete ou création)
  const [selectedCertificate, setSelectedCertificate] =
    useState<FgasCapacityCertificate | null>(null)

  // Vue 2 – formulaire
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [isAutoFilled, setIsAutoFilled] = useState(false)
  const [autoFilledCert, setAutoFilledCert] =
    useState<FgasCapacityCertificate | null>(null)
  const [certFile, setCertFile] = useState<File | null>(null)
  const nativeFileInputRef = useRef<HTMLInputElement>(null)

  // Recherche autocomplete
  const [searchResults, setSearchResults] = useState<FgasCapacityCertificate[]>(
    []
  )
  const [isSearching, setIsSearching] = useState(false)
  /** Un n° AC identique existe côté API (résultat recherche). */
  const [acNumberConflict, setAcNumberConflict] = useState(false)
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>()

  // Vue 3 – confirmation
  const [confirmed, setConfirmed] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  /** Après création API : au retour liste, recharger les attestations */
  const pendingListRefreshAfterCreateRef = useRef(false)

  useEffect(() => {
    if (!open) return
    resetState()
    if (!userEmail) return
    setLoadingCerts(true)
    getFgasCapacityCertificates(userEmail)
      .then(setCertificates)
      .catch(() => setCertificates([]))
      .finally(() => setLoadingCerts(false))
  }, [open, userEmail])

  const resetState = () => {
    setCertificates([])
    setView(isGuest ? 'create' : 'list')
    setSelectedCertificate(null)
    setForm(EMPTY_FORM)
    setIsAutoFilled(false)
    setAutoFilledCert(null)
    setCertFile(null)
    setSearchResults([])
    setAcNumberConflict(false)
    setShowAll(false)
    setConfirmed(false)
    setError('')
    pendingListRefreshAfterCreateRef.current = false
    if (nativeFileInputRef.current) nativeFileInputRef.current.value = ''
  }

  const resetCreateForm = () => {
    setForm(EMPTY_FORM)
    setIsAutoFilled(false)
    setAutoFilledCert(null)
    setCertFile(null)
    setSearchResults([])
    setAcNumberConflict(false)
    if (nativeFileInputRef.current) nativeFileInputRef.current.value = ''
  }

  const clearCertFile = () => {
    setCertFile(null)
    if (nativeFileInputRef.current) nativeFileInputRef.current.value = ''
  }

  const updateForm = (
    key: Exclude<keyof FormState, 'holderAddress'>,
    value: string
  ) => setForm((prev) => ({ ...prev, [key]: value }))

  const updateHolderAddress = (
    key: keyof FormState['holderAddress'],
    value: string
  ) =>
    setForm((prev) => ({
      ...prev,
      holderAddress: { ...prev.holderAddress, [key]: value },
    }))

  const handleCertNumberChange = (value: string) => {
    updateForm('certificateNumber', value)
    if (isAutoFilled) {
      setIsAutoFilled(false)
      setAutoFilledCert(null)
      setForm({ ...EMPTY_FORM, certificateNumber: value })
    }
    clearTimeout(searchTimerRef.current)
    if (value.length < 2) {
      setSearchResults([])
      setAcNumberConflict(false)
      setIsSearching(false)
      return
    }
    setIsSearching(true)
    searchTimerRef.current = setTimeout(async () => {
      try {
        const results = await searchFgasCapacityCertificates(value)
        setSearchResults(results)
        const conflict = results.some((r) => r.certificateNumber === value)
        setAcNumberConflict(conflict)
      } catch {
        setSearchResults([])
        setAcNumberConflict(false)
      } finally {
        setIsSearching(false)
      }
    }, 300)
  }

  const handleSelectSearchResult = (cert: FgasCapacityCertificate) => {
    setForm({
      certificateNumber: cert.certificateNumber,
      validityStartDate: cert.validityStartDate,
      validityEndDate: cert.validityEndDate,
      holderCompanyName: cert.holderCompanyName,
      holderSiret: cert.holderSiret,
      holderAddress: cert.holderAddress,
    })
    setAutoFilledCert(cert)
    setIsAutoFilled(true)
    setCertFile(null)
    setSearchResults([])
    setAcNumberConflict(false)
  }

  const handleSelectExisting = (cert: FgasCapacityCertificate) => {
    setSelectedCertificate(cert)
    setView('confirm')
  }

  const handleGoBack = () => {
    setConfirmed(false)
    setError('')
    if (view === 'confirm') {
      setSelectedCertificate(null)
      resetCreateForm()
      setView(isGuest ? 'create' : 'list')
      if (pendingListRefreshAfterCreateRef.current && userEmail) {
        pendingListRefreshAfterCreateRef.current = false
        setLoadingCerts(true)
        getFgasCapacityCertificates(userEmail)
          .then(setCertificates)
          .catch(() => {
            /* conserver la liste affichée si l’API échoue */
          })
          .finally(() => setLoadingCerts(false))
      }
    } else if (view === 'create') {
      if (isGuest) {
        resetCreateForm()
      } else {
        setView('list')
      }
    }
  }

  const isDuplicateAcNumber =
    !isAutoFilled &&
    form.certificateNumber.trim().length >= 2 &&
    acNumberConflict

  const manualFormatsInvalid =
    !isAutoFilled &&
    !(
      isValidFgasSiret(form.holderSiret) &&
      isValidFgasFrenchPostalCode(form.holderAddress.zipcode)
    )

  const datesInvalid =
    !isFgasStartStrictlyBeforeEnd(
      form.validityStartDate,
      form.validityEndDate
    ) || isFgasEndDateExpiredOrToday(form.validityEndDate)

  const isFormValid =
    !!form.certificateNumber.trim() &&
    !!form.validityStartDate &&
    !!form.validityEndDate &&
    !!form.holderCompanyName.trim() &&
    !!form.holderSiret.trim() &&
    !!form.holderAddress.address.trim() &&
    !!form.holderAddress.zipcode.trim() &&
    !!form.holderAddress.city.trim() &&
    (!!autoFilledCert || !!certFile) &&
    (isAutoFilled || !manualFormatsInvalid) &&
    !datesInvalid &&
    !isDuplicateAcNumber

  const siretShowError =
    !isAutoFilled &&
    !!form.holderSiret.trim() &&
    !isValidFgasSiret(form.holderSiret)

  const postalShowError =
    !isAutoFilled &&
    !!form.holderAddress.zipcode.trim() &&
    !isValidFgasFrenchPostalCode(form.holderAddress.zipcode)

  const dateOrderShowError =
    !!form.validityStartDate &&
    !!form.validityEndDate &&
    !isFgasStartStrictlyBeforeEnd(form.validityStartDate, form.validityEndDate)

  const endDateExpiredShowError =
    !!form.validityEndDate && isFgasEndDateExpiredOrToday(form.validityEndDate)

  /** Si les deux problèmes sont présents, on n’affiche que l’erreur d’ordre des dates. */
  const showEndDateExpiredError = endDateExpiredShowError && !dateOrderShowError

  const handleValidate = async () => {
    if (autoFilledCert) {
      setError('')
      setSelectedCertificate(autoFilledCert)
      resetCreateForm()
      setView('confirm')
      return
    }

    setSubmitting(true)
    setError('')
    try {
      const payload = {
        ...form,
        ...(userEmail ? { renter: { email: userEmail } } : {}),
      }
      const created = await createFgasCapacityCertificate(payload, certFile!)
      pendingListRefreshAfterCreateRef.current = true
      setSelectedCertificate(created)
      resetCreateForm()
      setView('confirm')
    } catch {
      setError('Une erreur est survenue. Veuillez réessayer.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleConfirm = async () => {
    if (!selectedCertificate) return

    setSubmitting(true)
    setError('')
    try {
      await onConfirm(selectedCertificate)
    } catch {
      setError(
        "Une erreur est survenue lors de l'enregistrement. Veuillez réessayer."
      )
      setSubmitting(false)
    }
  }

  const displayCert = selectedCertificate ?? form
  const currentCompanyName = displayCert.holderCompanyName
  const currentSiret = displayCert.holderSiret

  const addressLines = (() => {
    const { address, zipcode, city } = displayCert.holderAddress
    const line2 = `${zipcode} ${city}`.trim()
    return [address.trim(), line2].filter(Boolean)
  })()

  const displayedCerts = showAll
    ? certificates
    : certificates.slice(0, CERTS_INITIAL_COUNT)
  const hiddenCount = certificates.length - CERTS_INITIAL_COUNT

  // Textes CMS avec fallbacks
  const t = basketData

  /** Invité : pas de liste ; évite un frame vide tant que `view` vaut encore `list`. */
  const resolvedView: ModalView = isGuest && view === 'list' ? 'create' : view

  const description = (
    <Typography
      variant="body2"
      className={classes.description}
      color="textSecondary"
    >
      <CMSText
        asText
        data={t?.fgas_modal_description}
        defaultText="Au moins un produit de votre panier nécessite une mise en service par une société ayant une attestation de capacité en cours de validité."
      />
    </Typography>
  )

  const backButton = (
    <Button
      className={classes.backLink}
      startIcon={<ArrowBackIcon fontSize="small" />}
      onClick={handleGoBack}
    >
      <CMSText asText data={t?.fgas_back_button} defaultText="Retour" />
    </Button>
  )

  return (
    <GlobalModal
      open={open}
      onClose={onClose}
      customClass="fgas-certificate-modal"
      modalTitle={{
        data: t?.fgas_modal_title,
        defaultText: "Saisie de l'attestation de capacité",
      }}
    >
      {/* ── VUE 1 : liste des attestations (utilisateur connecté uniquement) ── */}
      {resolvedView === 'list' && (
        <Box>
          {description}

          {loadingCerts ? (
            <Box className={classes.loadingCenter}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              {certificates.length > 0 && (
                <>
                  <Typography variant="body2" className={classes.sectionTitle}>
                    <CMSText
                      asText
                      data={t?.fgas_companies_title}
                      defaultText="Mes sociétés enregistrées"
                    />
                  </Typography>

                  {displayedCerts.map((cert) => (
                    <Box
                      key={cert.id}
                      className={clsx(
                        classes.certCard,
                        selectedCertificate?.id === cert.id &&
                          classes.certCardSelected
                      )}
                      onClick={() => handleSelectExisting(cert)}
                    >
                      <ApartmentIcon className={classes.certIcon} />
                      <Box>
                        <Typography
                          variant="body2"
                          className={classes.certName}
                        >
                          {cert.holderCompanyName}
                        </Typography>
                        <Typography
                          variant="caption"
                          display="block"
                          className={classes.certMeta}
                        >
                          SIRET : {cert.holderSiret}
                        </Typography>
                        <Typography
                          variant="caption"
                          display="block"
                          className={classes.certMeta}
                        >
                          {cert.certificateNumber}
                          {cert.validityEndDate
                            ? ` — Valide jusqu'au ${cert.validityEndDate}`
                            : ''}
                        </Typography>
                      </Box>
                    </Box>
                  ))}

                  {hiddenCount > 0 && (
                    <Button
                      className={classes.showMoreBtn}
                      size="small"
                      startIcon={
                        showAll ? <ExpandLessIcon /> : <ExpandMoreIcon />
                      }
                      onClick={() => setShowAll((v) => !v)}
                    >
                      {showAll ? (
                        <CMSText
                          asText
                          data={t?.fgas_show_less}
                          defaultText="Voir moins"
                        />
                      ) : (
                        <>
                          <CMSText
                            asText
                            data={t?.fgas_show_more}
                            defaultText="Voir plus"
                          />
                          {` (${hiddenCount} autre${
                            hiddenCount > 1 ? 's' : ''
                          })`}
                        </>
                      )}
                    </Button>
                  )}
                </>
              )}

              <Button
                className={classes.addNewBtn}
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setView('create')}
              >
                <CMSText
                  asText
                  data={t?.fgas_add_new_button}
                  defaultText="Ajouter une nouvelle attestation de capacité"
                />
              </Button>

              <Box className={classes.cancelRow}>
                <Button onClick={onClose}>
                  <CMSText
                    asText
                    data={t?.fgas_cancel_button}
                    defaultText="Annuler"
                  />
                </Button>
              </Box>
            </>
          )}
        </Box>
      )}

      {/* ── VUE 2 : formulaire de création ── */}
      {resolvedView === 'create' && (
        <Box className={classes.formVerticalStack}>
          {description}
          {!isGuest && backButton}

          <Box>
            {/* N° attestation – champ de recherche */}
            <label
              className={clsx(classes.inputLabel, 'label')}
              htmlFor="fgas-certificate-number"
            >
              <CMSText
                asText
                data={t?.fgas_certificate_number_label}
                defaultText="Numéro d'attestation de capacité (AC)"
              />{' '}
              *
            </label>
            <Box className={classes.searchWrapper}>
              <Box className={classes.certSearchRow}>
                <Box className={classes.certSearchIcon}>
                  {isSearching ? (
                    <CircularProgress size={16} />
                  ) : (
                    <SearchIcon color="inherit" fontSize="small" />
                  )}
                </Box>
                <TextField
                  className={classes.certSearchFieldInner}
                  name="fgas-certificate-number"
                  placeholder={prismicText(
                    t?.fgas_certificate_number_placeholder,
                    'Rechercher ou saisir un n° AC…'
                  )}
                  value={form.certificateNumber}
                  onChange={handleCertNumberChange}
                  noMargin
                />
              </Box>
              {searchResults.length > 0 && (
                <Paper className={classes.searchDropdown} elevation={4}>
                  {isDuplicateAcNumber && (
                    <Box px={2} pt={1}>
                      <Typography
                        variant="caption"
                        display="block"
                        className={classes.error}
                      >
                        Ce numéro d&apos;AC existe déjà. Veuillez sélectionner
                        l&apos;AC existante ci-dessous.
                      </Typography>
                    </Box>
                  )}
                  <List dense disablePadding>
                    {searchResults.map((r) => (
                      <ListItem
                        key={r.id}
                        button
                        onClick={() => handleSelectSearchResult(r)}
                      >
                        <ListItemText
                          primary={r.certificateNumber}
                          secondary={r.holderCompanyName}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}
            </Box>
          </Box>

          {/* Raison sociale + SIRET */}
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={
                  <>
                    <CMSText
                      asText
                      data={t?.fgas_company_name_label}
                      defaultText="Raison sociale"
                    />{' '}
                    *
                  </>
                }
                name="fgas-holder-company"
                type="text"
                value={form.holderCompanyName}
                disabled={isAutoFilled}
                onChange={(value) => updateForm('holderCompanyName', value)}
                noMargin
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={
                  <>
                    <CMSText
                      asText
                      data={t?.fgas_siret_label}
                      defaultText="SIRET"
                    />{' '}
                    *
                  </>
                }
                name="fgas-holder-siret"
                type="text"
                value={form.holderSiret}
                disabled={isAutoFilled}
                onChange={(value) => updateForm('holderSiret', value)}
                maxLength={14}
                noMargin
              />
              {siretShowError && (
                <Typography
                  variant="caption"
                  display="block"
                  className={classes.error}
                >
                  Le SIRET doit contenir 14 chiffres
                </Typography>
              )}
            </Grid>
          </Grid>

          {/* Adresse */}
          <TextField
            label={
              <>
                <CMSText
                  asText
                  data={t?.fgas_address_label}
                  defaultText="Adresse"
                />{' '}
                *
              </>
            }
            name="fgas-holder-address"
            type="text"
            value={form.holderAddress.address}
            disabled={isAutoFilled}
            onChange={(value) => updateHolderAddress('address', value)}
            noMargin
          />

          <Grid container spacing={1}>
            <Grid item xs={12} sm={4}>
              <TextField
                label={
                  <>
                    <CMSText
                      asText
                      data={t?.fgas_postal_code_label}
                      defaultText="Code postal"
                    />{' '}
                    *
                  </>
                }
                name="fgas-holder-postcode"
                type="text"
                value={form.holderAddress.zipcode}
                disabled={isAutoFilled}
                onChange={(value) => updateHolderAddress('zipcode', value)}
                maxLength={5}
                noMargin
              />
              {postalShowError && (
                <Typography
                  variant="caption"
                  display="block"
                  className={classes.error}
                >
                  Le code postal doit contenir 5 chiffres
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label={
                  <>
                    <CMSText
                      asText
                      data={t?.fgas_city_label}
                      defaultText="Ville"
                    />{' '}
                    *
                  </>
                }
                name="fgas-holder-city"
                type="text"
                value={form.holderAddress.city}
                disabled={isAutoFilled}
                onChange={(value) => updateHolderAddress('city', value)}
                noMargin
              />
            </Grid>
          </Grid>

          {/* Date début + Date fin */}
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={
                  <>
                    <CMSText
                      asText
                      data={t?.fgas_start_date_label}
                      defaultText="Date début validité AC"
                    />{' '}
                    *
                  </>
                }
                name="fgas-validity-start"
                type="date"
                value={form.validityStartDate}
                disabled={isAutoFilled}
                onChange={(value) => updateForm('validityStartDate', value)}
                noMargin
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={
                  <>
                    <CMSText
                      asText
                      data={t?.fgas_end_date_label}
                      defaultText="Date d'expiration AC"
                    />{' '}
                    *
                  </>
                }
                name="fgas-validity-end"
                type="date"
                value={form.validityEndDate}
                disabled={isAutoFilled}
                onChange={(value) => updateForm('validityEndDate', value)}
                noMargin
              />
            </Grid>
          </Grid>
          {dateOrderShowError && (
            <Typography variant="body2" className={classes.error}>
              La date de début doit être antérieure à la date de fin
            </Typography>
          )}
          {showEndDateExpiredError && (
            <Typography variant="body2" className={classes.error}>
              L&apos;attestation de capacité est expirée ou expire
              aujourd&apos;hui et ne peut pas être utilisée
            </Typography>
          )}

          <Box>
            {/* Document justificatif */}
            <label
              className={clsx(classes.inputLabel, 'label')}
              htmlFor="fgas-cert-file-input"
            >
              <CMSText
                asText
                data={t?.fgas_document_label}
                defaultText="Document AC (pdf, jpeg, png)"
              />
              {!isAutoFilled && <> *</>}
            </label>
            <Box
              className={clsx(
                classes.fileUploadArea,
                isAutoFilled && classes.fileUploadAreaDisabled
              )}
              onClick={() => {
                if (!isAutoFilled) nativeFileInputRef.current?.click()
              }}
            >
              <Box className={classes.fileUploadInner}>
                <CloudUploadIcon color="action" fontSize="small" />
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className={classes.fileUploadText}
                  noWrap
                >
                  {certFile ? (
                    certFile.name
                  ) : (
                    <CMSText
                      asText
                      data={t?.fgas_document_placeholder}
                      defaultText="Cliquez pour sélectionner un fichier"
                    />
                  )}
                </Typography>
                {certFile && !isAutoFilled && (
                  <IconButton
                    type="button"
                    size="small"
                    className={classes.fileUploadClear}
                    aria-label="Retirer le fichier"
                    onClick={(e) => {
                      e.stopPropagation()
                      clearCertFile()
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
            <input
              id="fgas-cert-file-input"
              ref={nativeFileInputRef}
              type="file"
              hidden
              disabled={isAutoFilled}
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => setCertFile(e.target.files?.[0] ?? null)}
            />
          </Box>

          {error && (
            <Typography variant="body2" className={classes.error}>
              {error}
            </Typography>
          )}

          <Button
            className={classes.validateBtn}
            variant="contained"
            color="primary"
            disabled={!isFormValid || submitting}
            onClick={handleValidate}
          >
            {submitting && <CircularProgress size={18} color="inherit" />}
            <span className={clsx({ [classes.loadingBtnLabel]: submitting })}>
              <CMSText
                asText
                data={t?.fgas_validate_button}
                defaultText="Valider l'attestation"
              />
            </span>
          </Button>
        </Box>
      )}

      {/* ── VUE 3 : confirmation ── */}
      {resolvedView === 'confirm' && (
        <Box className={classes.formVerticalStack}>
          {description}
          {backButton}

          {/* Résumé de l'attestation */}
          <Box className={classes.confirmCard}>
            <Typography variant="body2" className={classes.confirmCompanyName}>
              {currentCompanyName}
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ whiteSpace: 'pre-line' }}
            >
              {addressLines.join('\n')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              SIRET : {currentSiret}
            </Typography>
          </Box>

          {/* Checkbox d'engagement */}
          <Box className={classes.checkboxContainer}>
            <FormControlLabel
              className={classes.checkboxLabel}
              control={
                <Checkbox
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  <CMSText
                    data={t?.fgas_certificate_checkbox}
                    defaultText={`Je m'engage à faire réaliser la mise en service du ou des produits F-Gas présents dans mon panier par la société `}
                  />
                  {!t?.fgas_certificate_checkbox && (
                    <>
                      <strong>{currentCompanyName}</strong>` ($
                      {addressLines.join(', ')})` .
                    </>
                  )}
                </Typography>
              }
            />
          </Box>

          {error && (
            <Typography variant="body2" className={classes.error}>
              {error}
            </Typography>
          )}

          <Box className={classes.confirmActions}>
            <Button
              className={classes.confirmBackBtn}
              variant="outlined"
              onClick={handleGoBack}
            >
              <CMSText asText data={t?.fgas_back_button} defaultText="Retour" />
            </Button>
            <Button
              className={classes.confirmBtn}
              variant="contained"
              color="primary"
              disabled={!confirmed || submitting}
              onClick={handleConfirm}
            >
              {submitting && <CircularProgress size={18} color="inherit" />}
              <span className={clsx({ [classes.loadingBtnLabel]: submitting })}>
                <CMSText
                  asText
                  data={t?.fgas_confirm_button}
                  defaultText="Confirmer et continuer"
                />
              </span>
            </Button>
          </Box>
        </Box>
      )}
    </GlobalModal>
  )
}

export default FgasCertificateModal
